/**
 * Scales <-> Chart switch, shared by the PDP Performance section and the
 * product card performance block.
 *
 * Both views render the same metric blocks; only their grid placement, the
 * gauge rings and the radar chart differ. Switching therefore animates as a FLIP:
 * the layout is applied immediately, then each metric is transformed back to where
 * it was and released, so the labels glide between the row and the chart arrangement.
 *
 * A host may also carry axes the chart plots but the scales leave out — the card
 * grades three and charts four. Those are marked `data-perf-chart-only`. Having no
 * place in the other view they have nowhere to glide from, so instead they fade on
 * the diagram's clock and drift a little along the spoke each one names.
 *
 * An axis can equally be one whose two placements are too far apart to glide
 * between. The PDP sets all four out in a row in Scales and takes the lower pair
 * below the diagram in Chart, so rather than drag two labels the length of the
 * chart as it appears, those axes render twice: the graded metric, marked
 * `data-perf-echoed`, and a `data-perf-echo` copy of it. Where the stylesheet
 * renders the copy — a decision left entirely to it — the switch cross-fades the
 * two in place. Both halves fade on the diagram's clock like a chart-only label.
 * The one in the row steps aside to the right and shrinks its ring away as it
 * goes — the same shrink the rings that fade alone make — and returns from there,
 * so the pair reads as leaving the row rather than blinking out of it; the copy
 * below the diagram rides the lower edge of it, down as it grows and back up as
 * it contracts, so the two read as one thing opening and closing.
 *
 * Parts are found through data attributes rather than class names so each host can
 * keep its own BEM block. Timings mirror the --perf-duration / --perf-ease pair that
 * every host declares.
 */

const DURATION = 480;
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const GAUGE_OUT = 200;
const GAUGE_IN = 260;
// How far down a ring is drawn at the transparent end of its fade. Every ring
// makes the same shrink, whether it fades on the rings' own clock or is carried
// off by a label that is fading around it.
const GAUGE_SHRINK = 0.7;
const CHART_IN = 340;
// The way out runs the full switch so the fade keeps pace with the space closing
// around the chart. A shorter one left the diagram gone by the time a third of the
// gap had closed, and the rest of the collapse then read as a lag rather than as
// part of the same movement.
const CHART_OUT = DURATION;
// The size the diagram is drawn at while transparent, at either end of the switch.
// Also what the labels below it read to follow its lower edge, so the two cannot
// drift apart.
const CHART_HIDDEN_SCALE = 0.86;
// How far a chart-only label sits back along its spoke at the transparent end of
// its fade. Read against the corner's own reach from the centre — roughly a
// quarter of it — so the label plainly travels out of the diagram while still
// starting from somewhere over it rather than from open space.
const CHART_ONLY_DRIFT = 40;
// How far to the right the graded half of an echoed pair sits at the transparent
// end of its cross-fade, so it leaves the scales row to the right and arrives back
// from it rather than blinking out of the row it shares. A nudge across a swap
// rather than a journey out of a diagram, hence well short of the spoke. The copy
// under the chart takes no step sideways at all — it answers to the diagram's
// lower edge instead, and the two movements would only fight each other.
const ECHOED_DRIFT = 24;

/**
 * The collection's presentation grid stacks every product's info column in the
 * same cell and cross-fades between them as you scroll, so the switch reads as
 * one persistent control even though each product owns its own instance. A
 * choice therefore has to carry to the rest of the group, including cards that
 * only arrive later from pagination.
 *
 * Hosts opt in with `data-perf-sync`, and only instances sharing a value follow
 * each other — a PDP section is a single deliberate control and has no business
 * being switched by a card grid that happens to sit on the same page.
 *
 * @type {Map<string, string>} group name -> the view last chosen in it
 */
const groupViews = new Map();
/** @type {Set<PerformanceViews>} */
const syncedInstances = new Set();

/**
 * The part of a metric the eye actually tracks. The box around it is padded to
 * clear a gauge ring in one view and not in the other, so it is the wrong thing
 * to measure when the question is whether a label appears to have moved.
 *
 * @param {HTMLElement} metric
 * @returns {Element}
 */
const labelBody = (metric) =>
  metric.querySelector("[data-perf-text]") ?? metric;

/**
 * Whether the stylesheet is giving this element a box at all. The distinction
 * that matters is `display: none`: a part the current view keeps but does not
 * show is taken out of flow and turned transparent, which still generates boxes.
 *
 * It is what the switch asks instead of repeating a media query of its own, so
 * the widths each arrangement belongs to stay in the stylesheet.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isRendered = (element) => element.getClientRects().length > 0;

class PerformanceViews extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {Animation[]} */
  #animations = [];
  /** @type {HTMLElement | null} */
  #switch = null;
  /** @type {HTMLElement | null} */
  #viz = null;
  /** @type {HTMLElement | null} */
  #chart = null;
  /** @type {SVGElement | null} */
  #plot = null;
  /** @type {HTMLElement[]} */
  #options = [];
  /** @type {HTMLElement[]} */
  #metrics = [];
  /** @type {HTMLElement[][]} */
  #texts = [];
  /** @type {HTMLElement[]} */
  #gauges = [];
  /** @type {HTMLElement[]} */
  #chartOnly = [];
  /** @type {HTMLElement[]} */
  #echoes = [];
  /** @type {HTMLElement[]} */
  #echoed = [];

  connectedCallback() {
    this.#switch = this.querySelector("[data-perf-switch]");
    this.#viz = this.querySelector("[data-perf-viz]");
    if (!this.#switch || !this.#viz) return;

    this.#options = [...this.querySelectorAll("[data-perf-view]")];
    this.#metrics = [...this.querySelectorAll("[data-perf-metric]")];
    this.#texts = this.#metrics.map((metric) => [
      ...metric.querySelectorAll("[data-perf-text]"),
    ]);
    this.#gauges = [...this.querySelectorAll("[data-perf-gauge]")];
    // Axes the chart plots but the scales do not grade. They are not in the
    // FLIP: with no position in the other view there is nowhere to travel from,
    // so they fade on the chart's schedule instead.
    this.#chartOnly = [...this.querySelectorAll("[data-perf-chart-only]")];
    // The two halves of an axis that reads in a different place in each view.
    // Whether they are in play is the stylesheet's call, asked per switch rather
    // than settled here: the PDP only spreads its scales row wide enough to be
    // worth cross-fading from above 1200px.
    this.#echoes = [...this.querySelectorAll("[data-perf-echo]")];
    this.#echoed = [...this.querySelectorAll("[data-perf-echoed]")];
    this.#chart = this.querySelector("[data-perf-chart]");
    this.#plot = this.#chart?.querySelector(".performance-chart__plot") ?? null;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#switch.addEventListener("click", this.#handleClick, { signal });
    this.#switch.addEventListener("keydown", this.#handleKeydown, { signal });

    // Adopted before the first paint: a card scrolled into view on the default
    // would otherwise show the scales for a frame and then switch under the user.
    const group = this.dataset.perfSync;
    if (group) {
      syncedInstances.add(this);
      const shared = groupViews.get(group);
      if (shared && shared !== this.dataset.view) this.#applyView(shared);
    }

    this.#syncThumb();

    // Font loading and locale changes both resize the labels under the thumb.
    this.#resizeObserver = new ResizeObserver(() => this.#syncThumb());
    this.#resizeObserver.observe(this.#switch);

    requestAnimationFrame(() => this.#switch?.setAttribute("data-ready", ""));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#cancelAnimations();
    syncedInstances.delete(this);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const option = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest("[data-perf-view]")
        : null
    );
    if (!option?.dataset.perfView) return;
    this.#setView(option.dataset.perfView);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[
      event.key
    ];
    if (!step) return;

    event.preventDefault();
    const current = this.#options.findIndex(
      (option) => option.dataset.perfView === this.dataset.view,
    );
    const next =
      this.#options[
        (current + step + this.#options.length) % this.#options.length
      ];
    if (!next?.dataset.perfView) return;

    this.#setView(next.dataset.perfView);
    next.focus();
  };

  /** @param {string} view */
  #setView(view) {
    if (view === this.dataset.view || !this.#viz) return;

    this.#share(view);
    this.#cancelAnimations();

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.#applyView(view);
      return;
    }

    const toChart = view === "chart";
    // Every label that belongs to a single view, and so fades where it is rather
    // than gliding. The set leaving the screen is everything the view being
    // entered does not own.
    const solo = this.#soloLabels();
    const leaving = solo.filter((part) => part.view !== view);
    // A metric with an echo is read out of the FLIP in both directions: one of
    // the pair is always standing in for it, so it never travels — it fades in
    // where its own view puts it, or stays behind and fades out. Keyed by the
    // view each one belongs to, which is what says whether it is arriving or
    // leaving and so what clock anything inside it runs on.
    const fading = new Map(solo.map((part) => [part.element, part.view]));

    const firstMetrics = this.#metrics.map((metric) =>
      metric.getBoundingClientRect(),
    );
    const firstTexts = this.#texts.map((texts) =>
      texts.map((text) => text.getBoundingClientRect()),
    );
    const firstHeight = this.#viz.getBoundingClientRect().height;

    // The chart is centred on a container that is about to change size on both
    // axes: chart view caps its width, and the switch animates its height. Left
    // on its percentage anchors the centre would drift for the length of the
    // fade — sideways as the cap goes, and down or up with the height — so the
    // diagram would read as sliding into or out of place rather than growing and
    // shrinking where it sits. Frozen where it already is on the way out, and
    // below, once the layout has settled, on the way in.
    if (!toChart) this.#pinChart();

    // A label on its way out is still on screen once the switch has landed, but
    // the view it fades in front of takes it out of flow — a different placement,
    // resolved against a different box. Read where it sits while the layout it
    // belongs to still stands; it is put back below, once that layout has gone.
    const home = this.#measureSolo(leaving);
    // Same window, same reason: a label's spoke is the line out of the diagram's
    // centre, and on the way out the diagram is elsewhere once the switch lands.
    let drifts = toChart ? null : this.#drifts(solo);

    this.#applyView(view);

    this.#holdSolo(home);
    // The way in was waiting for exactly this: the diagram it drifts out of is
    // only in place now.
    drifts ??= this.#drifts(solo);

    const lastMetrics = this.#metrics.map((metric) =>
      metric.getBoundingClientRect(),
    );
    const lastTexts = this.#texts.map((texts) =>
      texts.map((text) => text.getBoundingClientRect()),
    );
    const lastHeight = this.#viz.getBoundingClientRect().height;

    // The centre the settled layout has just given it, which is the one it should
    // appear to grow from. Read here, before the first #play call, so the height
    // animation has yet to claim the box being measured.
    if (toChart) this.#pinChart();

    // The metrics are already at their final grid positions, so animating the
    // container's height only reflows the CTA and the rest of the page.
    this.#play(
      this.#viz,
      [{ height: `${firstHeight}px` }, { height: `${lastHeight}px` }],
      {
        duration: DURATION,
        easing: EASE,
      },
    );

    this.#metrics.forEach((metric, index) => {
      if (fading.has(metric)) return;

      const dx = firstMetrics[index].left - lastMetrics[index].left;
      const dy = firstMetrics[index].top - lastMetrics[index].top;

      if (dx || dy) {
        this.#play(
          metric,
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
          {
            duration: DURATION,
            easing: EASE,
          },
        );
      }

      // A metric's box is not the whole story: the title and value also shift
      // inside it. The gauge is out of flow, so each view reserves its space with
      // padding on the metric, and a view may align the two lines differently. Both
      // land the instant the view is applied, on a box the transform above only
      // carries around wholesale, so the text would jump while everything around it
      // glided. Warmth shows it at its plainest: its box holds the same cell in both
      // views, so there is no transform at all and the label moves by padding alone.
      //
      // Animating each line's own travel minus its box's leaves just the residual.
      // Both run on the same curve, so they compose back into the line's real path.
      this.#texts[index].forEach((text, line) => {
        const tx =
          firstTexts[index][line].left - lastTexts[index][line].left - dx;
        const ty =
          firstTexts[index][line].top - lastTexts[index][line].top - dy;
        if (!tx && !ty) return;

        this.#play(
          text,
          [{ transform: `translate(${tx}px, ${ty}px)` }, { transform: "none" }],
          {
            duration: DURATION,
            easing: EASE,
          },
        );
      });
    });

    const shrink = [
      { transform: "scale(1)" },
      { transform: `scale(${GAUGE_SHRINK})` },
    ];
    const fadeOut = [
      { opacity: 1, transform: "scale(1)" },
      { opacity: 0, transform: `scale(${GAUGE_SHRINK})` },
    ];

    for (const gauge of this.#gauges) {
      const owner = fading.get(gauge.closest("[data-perf-metric]"));

      // A ring belonging to a label that is itself fading has no fade of its
      // own — the label carries it, and the stylesheet keeps it opaque in the
      // view that hides the rest of them. It still shrinks away as it goes, the
      // same shrink every other ring makes, so the switch does one thing to the
      // rings rather than two. On its label's clock, not the rings', so the
      // shrink runs with the step sideways and the two read as one movement.
      if (owner) {
        const entering = owner === view;

        this.#play(gauge, entering ? [...shrink].reverse() : shrink, {
          duration: entering ? CHART_IN : CHART_OUT,
          delay: entering ? DURATION - CHART_IN : 0,
          easing: EASE,
          fill: "backwards",
        });
        continue;
      }

      this.#play(gauge, toChart ? fadeOut : [...fadeOut].reverse(), {
        duration: toChart ? GAUGE_OUT : GAUGE_IN,
        delay: toChart ? 0 : DURATION - GAUGE_IN,
        easing: EASE,
        fill: "backwards",
      });
    }

    // Timed with the chart rather than with the labels around them: a name has no
    // business being on screen before the spoke it reads, or after it.
    let leavingFade = null;
    for (const { element, view: owner } of solo) {
      const entering = owner === view;
      const inward = { opacity: 0, transform: drifts.get(element) ?? "none" };
      const placed = { opacity: 1, transform: "none" };

      const fade = this.#play(
        element,
        entering ? [inward, placed] : [placed, inward],
        {
          duration: entering ? CHART_IN : CHART_OUT,
          delay: entering ? DURATION - CHART_IN : 0,
          easing: EASE,
          fill: "backwards",
        },
      );

      if (!entering) leavingFade = fade;
    }

    // They all run to the same clock, so whichever lands last speaks for the set.
    if (leavingFade) {
      leavingFade.addEventListener("finish", () => this.#releaseSolo());
    }

    if (!this.#chart) return;

    const chartHidden = {
      opacity: 0,
      transform: `translate(-50%, -50%) scale(${CHART_HIDDEN_SCALE})`,
    };
    const chartShown = {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1)",
    };

    // The same movement either way, now that it is pinned: the diagram grows from
    // and contracts back into the one centre. It used to contract further than it
    // entered from, to keep the rings clear of the labels it sank past and of
    // whatever the closing gap brought up underneath it, and there is nothing
    // left for it to pass on the way down.
    const fade = this.#play(
      this.#chart,
      toChart ? [chartHidden, chartShown] : [chartShown, chartHidden],
      {
        duration: toChart ? CHART_IN : CHART_OUT,
        delay: toChart ? DURATION - CHART_IN : 0,
        easing: EASE,
        fill: "backwards",
      },
    );

    fade.addEventListener("finish", () => this.#unpinChart());

    if (this.#plot && toChart) {
      this.#play(
        this.#plot,
        [{ transform: "scale(0.2)" }, { transform: "scale(1)" }],
        {
          duration: CHART_IN,
          delay: DURATION - CHART_IN,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          fill: "backwards",
        },
      );
    }
  }

  /**
   * Carries a choice to the rest of the group and records it for instances that
   * have yet to connect. The others are behind a fade or well off-screen, so they
   * take it as a plain state change: running the FLIP on them would spend a
   * 480ms animation measuring boxes nobody is looking at.
   *
   * @param {string} view
   */
  #share(view) {
    const group = this.dataset.perfSync;
    if (!group) return;

    groupViews.set(group, view);

    for (const instance of syncedInstances) {
      if (instance === this || instance.dataset.perfSync !== group) continue;
      if (instance.dataset.view !== view) instance.#applyView(view);
    }
  }

  /** @param {string} view */
  #applyView(view) {
    this.dataset.view = view;

    for (const option of this.#options) {
      const selected = option.dataset.perfView === view;
      option.setAttribute("aria-checked", String(selected));
      option.tabIndex = selected ? 0 : -1;
    }

    this.#syncThumb();
  }

  #syncThumb() {
    const selected = this.#options.find(
      (option) => option.getAttribute("aria-checked") === "true",
    );
    if (!selected || !this.#switch) return;

    this.#switch.style.setProperty(
      "--perf-thumb-offset",
      `${selected.offsetLeft}px`,
    );
    this.#switch.style.setProperty(
      "--perf-thumb-width",
      `${selected.offsetWidth}px`,
    );
  }

  /**
   * Restates the chart's current centre as pixel offsets. Identical on screen to
   * the percentage anchors they replace, but no longer tied to a container whose
   * size is about to change.
   */
  #pinChart() {
    if (!this.#chart || !this.#viz) return;

    const chart = this.#chart.getBoundingClientRect();
    const viz = this.#viz.getBoundingClientRect();
    this.#chart.style.insetInlineStart = `${chart.left - viz.left + chart.width / 2}px`;
    this.#chart.style.insetBlockStart = `${chart.top - viz.top + chart.height / 2}px`;
  }

  #unpinChart() {
    if (!this.#chart) return;

    this.#chart.style.insetInlineStart = "";
    this.#chart.style.insetBlockStart = "";
  }

  /**
   * Every label that belongs to a single view, and so fades where it is rather
   * than gliding: there is no counterpart position in the other view for it to
   * travel from. Where each one stands at its transparent end is named by
   * `drift`: a chart-only label follows the spoke it reads, the graded half of an
   * echoed pair steps out of the scales row to the right, and the copy under the
   * chart holds to the diagram's lower edge and is carried by it.
   *
   * A pair counts for nothing at a width where the stylesheet leaves the echo out
   * of the document: the metric is then the only copy there is, and glides.
   *
   * @returns {{ element: HTMLElement, view: string, drift: 'spoke' | 'edge' | 'inline' }[]}
   */
  #soloLabels() {
    const parts = this.#chartOnly.map((element) => ({
      element,
      view: "chart",
      drift: /** @type {const} */ ("spoke"),
    }));

    if (!this.#echoes.some(isRendered)) return parts;

    return [
      ...parts,
      ...this.#echoes.map((element) => ({
        element,
        view: "chart",
        drift: /** @type {const} */ ("edge"),
      })),
      ...this.#echoed.map((element) => ({
        element,
        view: "scales",
        drift: /** @type {const} */ ("inline"),
      })),
    ];
  }

  /**
   * Where each of these labels stands at the transparent end of its fade, as the
   * transform that puts it there.
   *
   * A spoke drift sets the label back towards the middle of the diagram, along
   * the line running from the diagram's centre out to its own, and so is only
   * true while the chart layout stands — before the switch on the way out and
   * after it on the way in. It is read off the rendered boxes, so a label answers
   * to the corner it was actually given rather than to one assumed for it here.
   * An edge drift is the distance the diagram's lower edge sweeps as it grows to
   * full size, which the labels under it ride down into place rather than sitting
   * still while the chart opens past them.
   *
   * An inline drift is the same step rightwards whatever the layout, and answers
   * to nothing. A label with no drift named simply fades where it is.
   *
   * @param {{ element: HTMLElement, drift: 'spoke' | 'edge' | 'inline' | null }[]} parts
   * @returns {Map<HTMLElement, string>}
   */
  #drifts(parts) {
    /** @type {Map<HTMLElement, string>} */
    const drifts = new Map();

    for (const { element, drift } of parts) {
      if (drift === "inline") {
        drifts.set(element, `translate(${ECHOED_DRIFT}px, 0)`);
      }
    }

    const chart = this.#chart?.getBoundingClientRect();
    if (!chart) return drifts;

    const centreX = chart.left + chart.width / 2;
    const centreY = chart.top + chart.height / 2;
    // Half the growth, because the diagram grows from its own centre and only the
    // bottom half of it travels downwards.
    const sweep = (chart.height / 2) * (1 - CHART_HIDDEN_SCALE);

    for (const { element, drift } of parts) {
      if (drift === "edge") {
        drifts.set(element, `translate(0, ${-sweep}px)`);
        continue;
      }

      if (drift !== "spoke") continue;

      const label = labelBody(element).getBoundingClientRect();
      const x = label.left + label.width / 2 - centreX;
      const y = label.top + label.height / 2 - centreY;
      // A label sitting dead on the centre has no spoke to name a direction.
      const reach = Math.hypot(x, y);
      if (!reach) continue;

      const step = CHART_ONLY_DRIFT / reach;
      drifts.set(element, `translate(${-x * step}px, ${-y * step}px)`);
    }

    return drifts;
  }

  /**
   * Where each of these labels reads right now: the width its lines were set to,
   * and the box the eye is actually tracking.
   *
   * @param {{ element: HTMLElement }[]} parts
   * @returns {{ element: HTMLElement, width: number, text: DOMRect }[]}
   */
  #measureSolo(parts) {
    return parts.map(({ element }) => ({
      element,
      width: element.getBoundingClientRect().width,
      text: labelBody(element).getBoundingClientRect(),
    }));
  }

  /**
   * Holds a label where the view it belongs to left it, so it can fade without
   * moving. Deliberately measured after the switch rather than pinned to
   * coordinates worked out before it: whatever the new view does to the label —
   * pads it, shrinks it, aligns it the other way — the difference is read off the
   * rendered box and taken back out again.
   *
   * @param {{ element: HTMLElement, width: number, text: DOMRect }[]} home
   */
  #holdSolo(home) {
    for (const { element, width, text } of home) {
      // Out of flow the box would shrink to its text and re-lay the lines
      // inside it, so it keeps the width they were written at.
      element.style.inlineSize = `${width}px`;

      const now = labelBody(element).getBoundingClientRect();
      element.style.translate = `${text.left - now.left}px ${text.top - now.top}px`;
    }
  }

  #releaseSolo() {
    for (const element of [
      ...this.#chartOnly,
      ...this.#echoes,
      ...this.#echoed,
    ]) {
      element.style.inlineSize = "";
      element.style.translate = "";
    }
  }

  /**
   * @param {Element} element
   * @param {Keyframe[]} keyframes
   * @param {KeyframeAnimationOptions} options
   * @returns {Animation}
   */
  #play(element, keyframes, options) {
    const animation = element.animate(keyframes, options);
    this.#animations.push(animation);
    return animation;
  }

  #cancelAnimations() {
    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];
    // A switch back mid-fade leaves the pin and the hold behind otherwise.
    this.#unpinChart();
    this.#releaseSolo();
  }
}

if (!customElements.get("performance-views")) {
  customElements.define("performance-views", PerformanceViews);
}
